import React, { useState, useEffect, useMemo } from "react";
import "./ManageTicketPriority.css";

const API_URL = "https://server.majestyfx.com";

const ManageTicketPriority = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);
  const [adminRemark, setAdminRemark] = useState("");
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const [expandedTexts, setExpandedTexts] = useState({});

  // Fetch tickets based on status filter
  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const requestBody = statusFilter === "all" ? {} : 
        statusFilter === "open" ? { status: "open" } : 
        { status: "close" };

      const response = await fetch(`${API_URL}/fetchtickets.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setTickets(data.tickets || []);
      } else {
        throw new Error("Failed to fetch tickets");
      }
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError(err.message);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  // Handle ticket action (open/close)
  const handleTicketAction = async () => {
    if (!selectedTicketId || !adminRemark.trim()) {
      alert("Please provide an admin remark");
      return;
    }

    try {
      setActionLoading(selectedTicketId);
      
      const requestBody = {
        ticket_id: parseInt(selectedTicketId),
        admin_remark: adminRemark.trim(),
        status: actionType === "close" ? 1 : 0
      };

      const response = await fetch(`${API_URL}/admin_ticketapprove.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 1) {
        alert(data.message || "Ticket updated successfully");
        setShowRemarkModal(false);
        setAdminRemark("");
        setSelectedTicketId(null);
        setActionType("");
        // Refresh tickets
        fetchTickets();
      } else {
        throw new Error(data.message || "Failed to update ticket");
      }
    } catch (err) {
      console.error("Error updating ticket:", err);
      // alert("Error updating ticket: " + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Open remark modal
  const openRemarkModal = (ticketId, action) => {
    setSelectedTicketId(ticketId);
    setActionType(action);
    setShowRemarkModal(true);
  };

  // Close remark modal
  const closeRemarkModal = () => {
    setShowRemarkModal(false);
    setAdminRemark("");
    setSelectedTicketId(null);
    setActionType("");
  };

  // Toggle text expansion
  const toggleTextExpansion = (key) => {
    setExpandedTexts(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Truncated text component
  const TruncatedText = ({ text, maxLength = 20, uniqueKey }) => {
    if (!text || text.length <= maxLength) {
      return <span>{text || "N/A"}</span>;
    }

    const isExpanded = expandedTexts[uniqueKey];
    const displayText = isExpanded ? text : text.substring(0, maxLength) + "...";

    return (
      <div>
        <span>{displayText}</span>
        <button
          className="see-more-btn"
          onClick={() => toggleTextExpansion(uniqueKey)}
          style={{
            marginLeft: '5px',
            padding: '2px 6px',
            fontSize: '11px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            cursor: 'pointer'
          }}
        >
          {isExpanded ? 'See less' : 'See more'}
        </button>
      </div>
    );
  };

  // Get status label
  const getStatusLabel = (status) => {
    return status === "0" ? "Open" : "Closed";
  };

  // Get status class
  const getStatusClass = (status) => {
    return status === "0" ? "status-open" : "status-closed";
  };

  // Get priority class
  const getPriorityClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "priority-high";
      case "medium":
        return "priority-medium";
      case "low":
        return "priority-low";
      default:
        return "priority-default";
    }
  };

  // Filter and search tickets
  const filteredTickets = useMemo(() => {
    let filtered = tickets;

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(ticket => 
        ticket.subject?.toLowerCase().includes(search) ||
        ticket.email?.toLowerCase().includes(search) ||
        ticket.type?.toLowerCase().includes(search) ||
        ticket.priority?.toLowerCase().includes(search) ||
        ticket.message?.toLowerCase().includes(search)
      );
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => new Date(b.Date) - new Date(a.Date));

    return filtered;
  }, [tickets, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredTickets.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentTickets = filteredTickets.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchTerm, entriesPerPage]);

  return (
    <div className="main-content">
      <div className="breadcrumb">
        Home / <span>Support Tickets</span>
      </div>
      <h2>Support Ticket Management</h2>

      <div className="page-header">
        <div>
          <h2>Manage Support Tickets</h2>
        </div>
        <div className="header-buttons">
          <button className="btn blue" onClick={fetchTickets}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Status Filter */}
      <div className="filter-section">
        <div className="status-filters">
          <label>Filter by Status:</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter-select"
          >
            <option value="all">All Tickets</option>
            <option value="open">Open Tickets</option>
            <option value="close">Closed Tickets</option>
          </select>
        </div>
      </div>

      <div className="card">
        <div className="top-controls">
          <div>
            Show{" "}
            <select 
              value={entriesPerPage} 
              onChange={(e) => setEntriesPerPage(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>{" "}
            entries
          </div>
          <div className="search-box">
            Search: 
            <input 
              type="text" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tickets..."
            />
          </div>
        </div>

        <div className="buttons">
          <button>Copy</button>
          <button>CSV</button>
          <button>Excel</button>
          <button>PDF</button>
          <button>Print</button>
          <button className="dropdown">Column visibility ▾</button>
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Ticket ID</th>
              <th>Email</th>
              <th>Subject</th>
              <th>Type</th>
              <th>Priority</th>
              <th>Message</th>
              <th>Status</th>
              <th>Date</th>
              <th>Admin Remark</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="loading">
                  <div className="loading-spinner">Loading tickets...</div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="10" className="error">
                  <div className="error-message">
                    Error: {error}
                    <button onClick={fetchTickets} className="retry-btn">
                      Retry
                    </button>
                  </div>
                </td>
              </tr>
            ) : currentTickets.length === 0 ? (
              <tr>
                <td colSpan="10" className="empty">
                  {filteredTickets.length === 0 && tickets.length > 0
                    ? "No tickets match your search criteria"
                    : "No tickets available"}
                </td>
              </tr>
            ) : (
              currentTickets.map((ticket, index) => (
                <tr key={ticket.id}>
                  <td>{startIndex + index + 1}</td>
                  <td className="ticket-id">#{ticket.id}</td>
                  <td className="email">{ticket.email || "N/A"}</td>
                  <td className="subject">
                    <div className="subject-text" title={ticket.subject}>
                      {ticket.subject || "N/A"}
                    </div>
                  </td>
                  <td className="type">{ticket.type || "N/A"}</td>
                  <td className="type">{ticket.priority || "N/A"}</td>
                  <td className="priority">
                    {/* <span className={`priority-badge ${getPriorityClass(ticket.priority)}`}>
                     
                    </span> */}
                     <TruncatedText 
                        text={ticket.message} 
                        maxLength={20} 
                        uniqueKey={`priority-${ticket.id}`} 
                      />
                  </td>
                  <td className="status">
                    <span className={`status-badge ${getStatusClass(ticket.status)}`}>
                      {getStatusLabel(ticket.status)}
                    </span>
                  </td>
                  <td className="date">
                    {ticket.Date ? new Date(ticket.Date).toLocaleDateString() : "N/A"}
                  </td>
                  <td className="admin-remark">
                    <div className="remark-text" title={ticket.AdminRemark}>
                      {ticket.AdminRemark || "No remark"}
                    </div>
                  </td>
                  <td className="actions">
                    <div className="action-buttons">
                      {ticket.status === "0" ? (
                        <button
                          className="btn btn-close"
                          onClick={() => openRemarkModal(ticket.id, "close")}
                          disabled={actionLoading === ticket.id}
                        >
                          {actionLoading === ticket.id ? "Processing..." : "Close"}
                        </button>
                      ) : (
                        <button
                          className="btn btn-open"
                          onClick={() => openRemarkModal(ticket.id, "open")}
                          disabled={actionLoading === ticket.id}
                        >
                          {actionLoading === ticket.id ? "Processing..." : "Reopen"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="table-footer">
          <p>
            Showing {currentTickets.length > 0 ? startIndex + 1 : 0} to{" "}
            {Math.min(endIndex, filteredTickets.length)} of {filteredTickets.length} entries
            {filteredTickets.length !== tickets.length && (
              <span> (filtered from {tickets.length} total entries)</span>
            )}
          </p>
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage >= totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Admin Remark Modal */}
      {showRemarkModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>
                {actionType === "close" ? "Close Ticket" : "Reopen Ticket"} #{selectedTicketId}
              </h3>
              <button className="modal-close" onClick={closeRemarkModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="adminRemark">Admin Remark *</label>
                <textarea
                  id="adminRemark"
                  value={adminRemark}
                  onChange={(e) => setAdminRemark(e.target.value)}
                  placeholder={`Enter your remark for ${actionType === "close" ? "closing" : "reopening"} this ticket...`}
                  rows={4}
                  className="remark-textarea"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={closeRemarkModal}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className={`btn ${actionType === "close" ? "btn-close" : "btn-open"}`}
                onClick={handleTicketAction}
                disabled={actionLoading || !adminRemark.trim()}
              >
                {actionLoading ? "Processing..." : 
                 actionType === "close" ? "Close Ticket" : "Reopen Ticket"}
              </button>
            </div>
          </div>
        </div>
      )}

      <footer>
        <p>
          Copyright © 2025 <span className="brand">Ample Markets</span>. All
          rights reserved. <span className="version">Version 2.0</span>
        </p>
      </footer>
    </div>
  );
};

export default ManageTicketPriority;
